// AISummaryCard — Phase 1 (2026-05-19).
//
// Renders the LLM-generated facility summary (or deterministic empty-state
// message) for a facility's Overview tab. Fetches from the facility-summary
// Edge Function on mount, caches via the Edge Function's 24h TTL, and offers
// a Refresh button that forces regeneration. Empty-state shows clickable
// missing-field chips that jump to the relevant tab (Info, etc.).
//
// Auth pattern mirrors src/modules/copilot/hooks/use-copilot.ts: pull the
// current Supabase JWT from getSupabaseToken() (minted by firebase-jwt-bridge).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Sparkles, AlertCircle } from 'lucide-react';
import { getSupabaseToken } from '../../shared/supabase';
import { Skeleton } from '../../shared/components';
import { errorMessage } from '../../shared/errors';

export interface FacilitySummaryResponse {
  facility_id: string;
  summary_text: string;
  completeness_pct: number;
  is_empty_state: boolean;
  missing_fields: string[];
  generated_at: string;
  from_cache?: boolean;
  stale?: boolean;
}

type SummaryTab =
  | 'overview' | 'activity' | 'floorplan' | 'supplies' | 'tasks' | 'actions' | 'info';

interface AISummaryCardProps {
  facility_id: string;
  onJumpToTab?: (tab: SummaryTab) => void;
}

// Map missing_fields rubric keys to a tab destination + a friendly label.
const MISSING_FIELD_META: Record<string, { label: string; tab: SummaryTab }> = {
  facility_details: { label: 'Facility details', tab: 'info' },
  facility_access: { label: 'Access & security', tab: 'info' },
  contacts: { label: 'Contacts', tab: 'info' },
  billing_contact: { label: 'Billing contact', tab: 'info' },
  current_sub: { label: 'Current sub', tab: 'info' },
  am_collect_recent: { label: 'Recent AM Collect', tab: 'actions' },
  am_collect_complete: { label: 'Complete AM Collect', tab: 'actions' },
  feedback_history: { label: 'Log feedback', tab: 'activity' },
  lat_lng: { label: 'Map coordinates', tab: 'info' },
  chemicals_or_equipment: { label: 'Chemicals/equipment', tab: 'info' },
};

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Updated ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `Updated ${days}d ago`;
}

async function fetchSummary(
  facility_id: string,
  force = false,
): Promise<FacilitySummaryResponse> {
  const token = getSupabaseToken();
  if (!token) throw new Error('not_authenticated');
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const url =
    `${base}/functions/v1/facility-summary?facility_id=${encodeURIComponent(facility_id)}` +
    (force ? '&refresh=1' : '');
  const resp = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`http_${resp.status}: ${text.slice(0, 200)}`);
  }
  return (await resp.json()) as FacilitySummaryResponse;
}

export function AISummaryCard({ facility_id, onJumpToTab }: AISummaryCardProps) {
  const [data, setData] = useState<FacilitySummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (force = false) => {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await fetchSummary(facility_id, force);
        setData(res);
      } catch (e) {
        setError(errorMessage(e, String(e)));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [facility_id],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
        <div className="mb-3 flex items-center gap-2 text-tcs-text-muted">
          <Sparkles className="h-5 w-5" />
          <span className="text-sm font-medium uppercase tracking-wide">AI Summary</span>
        </div>
        <Skeleton className="mb-2 h-4 w-3/4" />
        <Skeleton className="mb-2 h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
        <div className="flex items-center gap-2 text-tcs-text-secondary">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">Summary unavailable — try again.</span>
          <button
            onClick={() => void load(true)}
            className="ml-auto rounded-md border border-tcs-border bg-tcs-surface px-2 py-1 text-xs hover:bg-tcs-surface-hover"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const missingCount = data.missing_fields?.length ?? 0;
  const isEmpty = data.is_empty_state;
  const stale = data.stale === true;

  return (
    <div
      className={
        'rounded-xl border bg-tcs-bg p-5 ' +
        (isEmpty ? 'border-tcs-border opacity-90' : 'border-tcs-border')
      }
    >
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-tcs-text-muted" />
        <span className="text-sm font-medium uppercase tracking-wide text-tcs-text-muted">
          AI Summary
        </span>
        {stale && (
          <span
            title="Showing last-known-good (refresh failed)"
            className="ml-1 inline-block h-2 w-2 rounded-full bg-st-warn-solid"
          />
        )}
        <span className="ml-auto inline-flex items-center gap-2">
          <span className="rounded-full border border-tcs-border bg-tcs-surface px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-tcs-text-muted">
            {data.completeness_pct}% complete
            {missingCount > 0 ? ` · ${missingCount} missing` : ''}
          </span>
          <button
            onClick={() => void load(true)}
            disabled={refreshing}
            title="Regenerate summary"
            className="rounded-md border border-tcs-border bg-tcs-surface p-1.5 text-tcs-text-muted hover:bg-tcs-surface-hover disabled:opacity-50"
          >
            <RefreshCw className={'h-3.5 w-3.5 ' + (refreshing ? 'animate-spin' : '')} />
          </button>
        </span>
      </div>

      <p
        className={
          'text-sm leading-relaxed ' +
          (isEmpty ? 'text-tcs-text-secondary italic' : 'text-tcs-text')
        }
      >
        {data.summary_text}
      </p>

      {isEmpty && missingCount > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {data.missing_fields.map((key) => {
            const meta = MISSING_FIELD_META[key];
            const label = meta?.label ?? key;
            const tab = meta?.tab;
            const clickable = !!tab && !!onJumpToTab;
            return (
              <button
                key={key}
                disabled={!clickable}
                onClick={clickable && tab ? () => onJumpToTab!(tab) : undefined}
                className={
                  'rounded-full border px-2.5 py-1 text-xs ' +
                  (clickable
                    ? 'border-tcs-border bg-tcs-surface text-tcs-text hover:bg-tcs-surface-hover cursor-pointer'
                    : 'border-tcs-border bg-tcs-surface text-tcs-text-muted cursor-default')
                }
                title={clickable ? `Jump to ${String(tab)} tab to fill in ${label}` : label}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-3 text-[11px] text-tcs-text-muted">
        {relativeTime(data.generated_at)}
        {data.from_cache ? ' · cached' : ''}
      </div>
    </div>
  );
}
